import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { projects } from '../mock';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectModal from '../components/ProjectModal';
import SeoHead from '../components/SeoHead';
import LazyVideo from '../components/LazyVideo';

const CategoryProjectsPage = () => {
  const navigate = useNavigate();
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState(null);

  // Map URL slugs to category names
  const categoryMap = {
    'film-tv': 'Films & TV Series',
    'film-festivals': 'Industry & Cultural Programming',
    'sports-ceremonies': 'Sports Ceremonies',
    'national-celebrations': 'National Celebrations',
    'theatrical-shows': 'Theatrical Shows & Immersive Experiences',
    'exhibitions': 'Exhibitions & Immersive Experiences'
  };

  const categoryName = categoryMap[category];
  const categoryProjects = projects.filter(p => p.category === categoryName);

  // Group projects by status for Film & TV category
  const groupedByStatus = {};
  const statusOrder = ['Completed', 'In Development', 'Financing', 'In Development - Other Projects'];
  
  if (category === 'film-tv' && categoryProjects.some(p => p.status)) {
    statusOrder.forEach(status => {
      const projectsInStatus = categoryProjects.filter(p => p.status === status);
      if (projectsInStatus.length > 0) {
        groupedByStatus[status] = projectsInStatus;
      }
    });
  }

  const hasStatusGrouping = Object.keys(groupedByStatus).length > 0;

  // Scroll to top when page initially loads (without project parameter)
  useEffect(() => {
    if (!searchParams.get('project')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [category]); // Trigger when category changes

  // Auto-open project if specified in URL query parameter
  // Also close project if parameter is removed
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId) {
      const project = categoryProjects.find(p => p.id === parseInt(projectId));
      if (project) {
        setSelectedProject(project);
        // Scroll to top when opening project
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // If no project parameter, ensure modal is closed
      setSelectedProject(null);
    }
  }, [searchParams, categoryProjects]);

  // If category doesn't exist, redirect to all projects
  if (!categoryName) {
    navigate('/projects');
    return null;
  }

  const categoryDescriptions = {
    'film-tv': 'Film and television projects developed, produced and consulted on across international markets — features, documentaries and series in development, financing and release.',
    'film-festivals': 'Industry and cultural programming for international film festivals including Red Sea Film Festival, Ajyal and Doha Film Festival.',
    'sports-ceremonies': 'Opening and closing ceremonies and large-scale sports productions for international tournaments.',
    'national-celebrations': 'National day ceremonies, cultural celebrations and large-scale national events.',
    'theatrical-shows': 'Creative conception, direction and production of immersive theatrical experiences and live performances.',
    'exhibitions': 'Curation and production of immersive exhibitions, edutainment experiences and large-scale interactive installations.',
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://carolmezher.com/' },
      { '@type': 'ListItem', position: 2, name: 'Portfolio', item: 'https://carolmezher.com/projects' },
      { '@type': 'ListItem', position: 3, name: categoryName, item: `https://carolmezher.com/projects/${category}` },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SeoHead
        title={`${categoryName} – Carol Mezher Portfolio`}
        description={categoryDescriptions[category] || `Projects in ${categoryName} by Carol Mezher.`}
        path={`/projects/${category}`}
        ogType="website"
        jsonLd={breadcrumbJsonLd}
      />
      <Header />
      
      <section className="py-20 bg-pale-blue relative overflow-hidden min-h-screen">
        {/* Colorful accent */}
        <div className="absolute top-0 left-0 w-1/4 h-full bg-gradient-to-r from-terracotta/7 to-transparent"></div>
        
        <div className="container mx-auto px-8 md:px-16 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Back button */}
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-2 text-whale-blue hover:text-midnight-blue mb-8 font-montserrat font-semibold transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to All Projects</span>
            </button>

            {/* Category header */}
            <div className="mb-16">
              <h1 className="font-playfair font-bold text-5xl md:text-6xl text-midnight-blue mb-4">
                {categoryName}
              </h1>
              <div className="h-1 w-24 bg-whale-blue"></div>
            </div>

            {/* Projects in this category */}
            {categoryProjects.length > 0 ? (
              <div className="space-y-16">
                {hasStatusGrouping ? (
                  // Film & TV: Clustered by status
                  Object.entries(groupedByStatus).map(([status, statusProjects]) => (
                    <div key={status} className="space-y-12">
                      {/* Status header */}
                      <div className="flex items-center gap-6 mb-8">
                        <h2 className="font-playfair font-bold text-3xl md:text-4xl text-midnight-blue">
                          {status === 'In Development - Other Projects' ? 'Other Projects (In Development)' : status}
                        </h2>
                        <div className="flex-1 h-0.5 bg-whale-blue/32"></div>
                      </div>

                      {/* Projects in this status */}
                      {statusProjects.map((project, index) => (
                        <article
                          key={project.id}
                          className="group cursor-pointer"
                          onClick={() => {
                            setSelectedProject(project);
                            // Update URL with project parameter
                            navigate(`/projects/${category}?project=${project.id}`, { replace: true });
                          }}
                        >
                          <div className={`grid lg:grid-cols-12 gap-8 items-center ${
                            index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                          }`}>
                            {/* Image/Video with colorful overlay */}
                            <div
                              className={`lg:col-span-7 relative overflow-hidden rounded-sm shadow-xl ${
                                index % 2 === 1 ? 'lg:col-start-6' : ''
                              }`}
                            >
                              {/* 16:10 frame + object-contain across all cards
                                  so every project image renders at the same
                                  visual size without cropping. */}
                              <div className="relative aspect-[16/10] overflow-hidden bg-charcoal/5">
                                {project.video ? (
                                  project.video.includes('vimeo.com') ? (
                                    <div className="relative w-full h-full">
                                      <iframe
                                        src={project.video.replace('vimeo.com/', 'player.vimeo.com/video/').split('?')[0] + '?autoplay=1&loop=1&background=1&muted=1'}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen"
                                      />
                                    </div>
                                  ) : (
                                    <LazyVideo
                                      src={project.video}
                                      poster={project.image}
                                      className="absolute inset-0 w-full h-full"
                                      autoPlay
                                      loop
                                      muted
                                      playsInline
                                    />
                                  )
                                ) : (
                                  <img
                                    src={project.image}
                                    alt={project.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                                  />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-whale-blue/82 to-transparent opacity-80 group-hover:opacity-50 transition-opacity duration-500"></div>
                              </div>
                            </div>

                            {/* Content with vibrant accents */}
                            <div
                              className={`lg:col-span-5 ${
                                index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
                              }`}
                            >
                              <div className="bg-white p-10 rounded-sm shadow-xl border-l-4 border-midnight-blue">
                                <h3 className="font-playfair font-bold text-3xl md:text-4xl text-charcoal mb-3 group-hover:text-whale-blue transition-colors">
                                  {project.title}
                                </h3>
                                
                                <p className="text-sm text-whale-blue mb-6 font-montserrat font-semibold italic">
                                  {project.director}
                                </p>
                                
                                {/* Fact Sheet - Bullet Points */}
                                <ul className="space-y-2 mb-6 text-sm text-charcoal/70">
                                  {project.year && project.id !== 6 && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span>
                                        <span className="font-semibold text-charcoal">
                                          {project.year === "Advanced Development" ? "Status:" : "Year:"}
                                        </span>{' '}
                                        {project.year}
                                      </span>
                                    </li>
                                  )}
                                  {project.format && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span><span className="font-semibold text-charcoal">Format:</span> {project.format}</span>
                                    </li>
                                  )}
                                  {project.broadcaster && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span><span className="font-semibold text-charcoal">Broadcaster:</span> {project.broadcaster}</span>
                                    </li>
                                  )}
                                  {project.runtime && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span><span className="font-semibold text-charcoal">Runtime:</span> {project.runtime}</span>
                                    </li>
                                  )}
                                  {project.genre && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span><span className="font-semibold text-charcoal">Genre:</span> {project.genre}</span>
                                    </li>
                                  )}
                                  {project.countries && (
                                    <li className="flex items-start gap-2">
                                      <span className="text-whale-blue mt-0.5">•</span>
                                      <span><span className="font-semibold text-charcoal">Countries:</span> {project.countries}</span>
                                    </li>
                                  )}
                                </ul>
                                
                                <p className="text-base text-charcoal/70 leading-relaxed mb-6 line-clamp-3">
                                  {project.description}
                                </p>
                                
                                <button className="flex items-center gap-2 text-base font-montserrat font-bold text-midnight-blue group-hover:gap-4 transition-all">
                                  <span>View Full Details</span>
                                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" strokeWidth={3} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  ))
                ) : (
                  // Other categories: Regular listing
                  categoryProjects.map((project, index) => (
                    <article
                      key={project.id}
                      className="group cursor-pointer"
                      onClick={() => {
                        setSelectedProject(project);
                        // Update URL with project parameter
                        navigate(`/projects/${category}?project=${project.id}`, { replace: true });
                      }}
                    >
                      <div className={`grid lg:grid-cols-12 gap-8 items-center ${
                        index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                      }`}>
                        {/* Image/Video with colorful overlay */}
                        <div
                          className={`lg:col-span-7 relative overflow-hidden rounded-sm shadow-xl ${
                            index % 2 === 1 ? 'lg:col-start-6' : ''
                          }`}
                        >
                          {/* 16:10 frame + object-contain across all cards
                              so every project image renders at the same
                              visual size without cropping. */}
                          <div className="relative aspect-[16/10] overflow-hidden bg-charcoal/5">
                            {project.video ? (
                              project.video.includes('vimeo.com') ? (
                                <div className="relative w-full h-full">
                                  <iframe
                                    src={project.video.replace('vimeo.com/', 'player.vimeo.com/video/').split('?')[0] + '?autoplay=1&loop=1&background=1&muted=1'}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen"
                                  />
                                </div>
                              ) : (
                                <LazyVideo
                                  src={project.video}
                                  poster={project.image}
                                  className="absolute inset-0 w-full h-full"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                />
                              )
                            ) : (
                              <img
                                src={project.image}
                                alt={project.title}
                                loading="lazy"
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-whale-blue/82 to-transparent opacity-80 group-hover:opacity-50 transition-opacity duration-500"></div>
                          </div>
                        </div>

                        {/* Content with vibrant accents */}
                        <div
                          className={`lg:col-span-5 ${
                            index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
                          }`}
                        >
                          <div className="bg-white p-10 rounded-sm shadow-xl border-l-4 border-midnight-blue">
                            <h3 className="font-playfair font-bold text-3xl md:text-4xl text-charcoal mb-4 group-hover:text-whale-blue transition-colors">
                              {project.id === 11 ? (
                                <>
                                  UEFA Champions League Final 2016
                                  <br />
                                  Opening Ceremony
                                </>
                              ) : project.title.includes('\n') ? (
                                project.title.split('\n').map((line, index) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    {index < project.title.split('\n').length - 1 && <br />}
                                  </React.Fragment>
                                ))
                              ) : (
                                project.title
                              )}
                            </h3>
                            
                            {project.id !== 16 && project.id !== 17 && project.id !== 18 && project.id !== 19 && project.id !== 20 && project.category !== 'Sports Ceremonies' && project.role && (
                              <p className="text-sm uppercase tracking-wider text-whale-blue mb-5 font-montserrat font-bold">
                                {project.role}
                              </p>
                            )}
                            
                            {project.subtitle && (
                              <p className="text-lg font-montserrat font-bold text-whale-blue mb-4 leading-snug">
                                {project.subtitle}
                              </p>
                            )}
                            
                            <p className="text-base text-charcoal/70 leading-relaxed mb-6">
                              {project.description}
                            </p>
                            
                            <button className="flex items-center gap-2 text-base font-montserrat font-bold text-midnight-blue group-hover:gap-4 transition-all">
                              <span>View Project</span>
                              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" strokeWidth={3} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-xl text-charcoal/60 mb-8">
                  No projects available in this category yet.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-3 bg-midnight-blue hover:bg-midnight-blue/90 text-white px-10 py-5 rounded-sm font-montserrat font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
                >
                  <ArrowLeft size={20} />
                  <span>Explore Other Categories</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </section>

      <ProjectModal
        project={selectedProject}
        onClose={() => {
          setSelectedProject(null);
          // Remove project parameter from URL when closing
          navigate(`/projects/${category}`, { replace: true });
        }}
      />

      <Footer />
    </div>
  );
};

export default CategoryProjectsPage;
